/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    ContextualMenu,
    IContextualMenuItem,
    IContextualMenuListProps,
    IContextualMenuProps,
    IContextualMenuStyleProps,
    IContextualMenuStyles
} from '@fluentui/react/lib/ContextualMenu';
import { Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction, IRenderFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2, important } from 'src/core/themes/luisStylist';
import { noop } from 'src/core/utils/base';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';

export type EntityDropdownProps = {
    /**
     * Whether to hide or show the search text field.
     */
    hideTextField?: boolean;

    /**
     * Menu's search query.
     */
    query?: string;

    /**
     * Search text field placeholder.
     */
    textFieldPlaceholder?: string;

    /**
     * Callback function that gets invoked when the user types in the search text field.
     */
    onChangeQuery?: (query: string) => void;

    /**
     * Callback function that gets invoked when the user presses enter inside the search text field.
     */
    onSubmit?: () => void;

    /**
     * Actionable items displayed at the bottom of the list.
     * Note that those items will not be included in the scrolling list.
     */
    footerActionableItems?: IContextualMenuItem[];
} & IContextualMenuProps;

const messages = defineMessages({
    searchPlaceholder: {
        id: 'labeling.luisDEntityDropdown.searchPlaceholder',
        defaultMessage: 'Search for an entity'
    }
});

const theme = getScopedTheme('EntityDropdown');

const { getClassName } = getStylistV2('EntityDropdown');

const textFieldClassName = getClassName(
    'menuWrapper',
    important({
        borderBottom: theme.textFieldBorder
    })
);
const contextualMenuStyles = classNamesFunction<IContextualMenuStyleProps, IContextualMenuStyles>()({
    header: { fontSize: '14px' },
    root: { boxShadow: theme.menuBoxShadow, width: 270 }
});

/**
 * Wrapper around office fabric ContextMenu, that adds styling and option to have search box inside the menu.
 */
export const EntityDropdown = (props: EntityDropdownProps) => {
    const {
        query = '',
        onSubmit = noop,
        onChangeQuery = noop,
        textFieldPlaceholder,
        hideTextField = false,
        footerActionableItems = [],
        ...rest
    } = props;
    const { localize } = useLocalization();

    /**
     * A render function to wrap the menu items with a search functionality.
     * @param menuListProps
     * @param defaultRender
     */
    const handleRenderMenuList = (menuListProps: IContextualMenuListProps, defaultRender: IRenderFunction<IContextualMenuListProps>) => {
        const placeholder = textFieldPlaceholder ?? localize(messages.searchPlaceholder);

        const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === LuisKeyCodes.Enter) {
                onSubmit();
            }
        };

        return (
            <Stack>
                {!hideTextField && (
                    <LuisTextField
                        autoFocus
                        ariaLabel={placeholder}
                        placeholder={placeholder}
                        onAbort={() => onChangeQuery('')}
                        onChange={(_ev, text: string) => onChangeQuery(text)}
                        onKeyUp={handleKeyUp}
                        value={query}
                        underlined
                        styles={{ wrapper: textFieldClassName }}
                    />
                )}
                <Stack styles={{ root: { maxHeight: '300px', overflowY: 'auto' } }}>{defaultRender(menuListProps)}</Stack>
                {footerActionableItems && (
                    <Stack styles={{ root: { borderTop: `1px solid ${theme.secondaryColor}` } }}>
                        {defaultRender({ ...menuListProps, items: footerActionableItems })}
                    </Stack>
                )}
            </Stack>
        );
    };

    return (
        <ContextualMenu
            {...rest}
            shouldFocusOnMount
            onRenderMenuList={handleRenderMenuList}
            styles={mergeStyleSets(contextualMenuStyles, rest.styles)}
            calloutProps={{ preventDismissOnScroll: true, ...rest.calloutProps }}
        />
    );
};
